import React from 'react';
import clsx from 'clsx';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { Avatar } from '@components/basics/Images/Avatar';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, NftSaleInfoFragmentDoc } from '@graphql/reactQuery';
import { formatWeiToEthDisplayFree } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment NFTSaleInfo on ActivityFeedGroupCollectedRelease {
    amountPaidInWei
    mostRecentPurchasedFromUser {
      id
      publicAddress
      webappUri
      username
      artist {
        id
      }
      avatar {
        id
        url
      }
    }
    totalUsersPurchasedFrom
    totalOwnedEditions
  }
`);

export const NEW_SONG_CARD_AVATAR_SIZE = 32;

export const NftSaleInfo = ({ saleInfo }: { saleInfo: FragmentType<NftSaleInfoFragmentDoc> }) => {
  const data = getFragment(NftSaleInfoFragmentDoc, saleInfo);
  const {
    amountPaidInWei,
    mostRecentPurchasedFromUser: { publicAddress, webappUri, username, artist, avatar },
    totalOwnedEditions,
    totalUsersPurchasedFrom,
  } = data;
  const purchasedFromMultipleUsers = totalUsersPurchasedFrom > 1;

  return (
    <div
      className={clsx(
        'ml-2 mt-3 flex h-10 w-fit min-w-0 max-w-full items-center overflow-hidden rounded-full bg-black/30 backdrop-blur-lg',
        purchasedFromMultipleUsers ? 'px-3 py-1' : 'p-1 pr-4',
      )}
    >
      {!purchasedFromMultipleUsers && (
        <div className="mr-2 aspect-square h-8 w-8 flex-shrink-0 overflow-hidden rounded-full">
          <Avatar
            src={avatar?.url}
            size={NEW_SONG_CARD_AVATAR_SIZE}
            username={username}
            walletAddress={publicAddress}
            webappUri={webappUri}
          />
        </div>
      )}
      <InfoContainer>
        <TextWrapper>
          <p>From</p>
          <StyledAccessibleLink pathnameUri={webappUriPath(webappUri)}>
            <NameContainer>
              <h3 className="inline flex-shrink overflow-hidden text-ellipsis">{username}</h3>
              {artist && <VerifiedTooltip size={16} portal toolTipOffset={4} />}
              {purchasedFromMultipleUsers && (
                <h3>
                  and {totalUsersPurchasedFrom - 1} other{totalUsersPurchasedFrom > 2 && 's'}
                </h3>
              )}
            </NameContainer>
          </StyledAccessibleLink>
        </TextWrapper>
        <TextWrapper>
          <p>For</p>
          <h3>{formatWeiToEthDisplayFree({ weiValue: amountPaidInWei, maxDecimals: 4 })}</h3>
          {!!totalOwnedEditions && totalOwnedEditions > 1 && (
            <span className="inline flex-shrink overflow-hidden text-ellipsis whitespace-nowrap">
              ({totalOwnedEditions} editions)
            </span>
          )}
        </TextWrapper>
      </InfoContainer>
    </div>
  );
};

const InfoContainer = styled('div', {
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  flexShrink: 1,
  overflow: 'hidden',
});

const NameContainer = styled('div', {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 4,
});

const TextWrapper = styled('div', {
  display: 'flex',
  alignItems: 'baseline',
  gap: 4,

  p: {
    base: 'S',
    color: '$neutral300',
  },

  h3: {
    base: 'S',
    color: '$white',
    fontStyle: 'semibold',
    maxWidth: 100,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  span: {
    base: 'S',
    color: '$white',
    fontStyle: 'regular',
  },
});

const StyledAccessibleLink = styled(AccessibleLink, {
  flexShrink: 1,
  display: 'inline',
  overflow: 'hidden',
});
